import React from 'react';
import { get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

// React view component to render
import { checkoutConfirmOrder as CheckoutConfirmOrderView } from 'ui/views/widgets/checkoutConfirmOrder';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { CheckoutConfirmOrderService } from 'services/checkoutConfirmOrder';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { SIZES } from 'constants/common';
import * as backRoute from 'utils/backRoute';
import { getBackRouteForCheckoutResolver } from 'resolvers/getBackRouteForCheckout';
import { clearRxCheckoutState } from 'resolvers/checkoutState';

// get store
const store = getStore();


/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CheckoutConfirmOrder } from 'ui/containers/CheckoutConfirmOrder';
 *
 * // render
 * <CheckoutConfirmOrder propName='propValue'/>
 */
class _CheckoutConfirmOrder extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this._bind();

        const queryParams = queryString.parse( this.props.location.search );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            requestPayload: undefined,
            origin: get( queryParams, 'origin', 'memberManagement' ),
            errorMessage: false
        };

        // create service instance
        this.checkoutConfirmOrderService = new CheckoutConfirmOrderService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    _bind() {
        this.placeOrder = this.placeOrder.bind( this );
        this.cancelOrder = this.cancelOrder.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.moveToPreviousStep = this.moveToPreviousStep.bind( this );
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CheckoutConfirmOrder.fetchComponentData()' );

        // cancel ongoing request
        this.checkoutConfirmOrderService.cancel();

         // get ongoing call info from the redux store
         const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
         const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
         const memberId = get( this.props.requestPayload, 'context.memberId', undefined );
         const patientId = get( this.props.requestPayload, 'context.patientId', undefined );
         
        // fetch component data
        const { status, data } = await this.checkoutConfirmOrderService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: {
                    callId,
                    callType,
                    memberId,
                    patientId
                }
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                requestPayload: {
                    context: {
                        callId,
                        callType,
                        memberId,
                        patientId
                    }
                }
            } );
        }
    }

    /**
     * cancel order
     */
    async cancelOrder( { action } ) {
        log.debug( 'CheckoutConfirmOrder.cancelOrder()' );

        const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation ),
			iconName: 'close'
        } );
        
        if( shouldProceed ) {

            this.checkoutConfirmOrderService.cancel();

            // show page level preloader
            pageLevelPreloader.show();
    
            // get status and service data
            const { status } = await this.checkoutConfirmOrderService.cancelOrder( {
                url: action.url,
                data: {
                    ...get( this.state, 'requestPayload' )
                }
            } );
    
            // hide page level preloader
            pageLevelPreloader.hide();
    
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                const backRoutePayload = backRoute.decode( this.props.location );

                if( ! isEmpty( backRoutePayload ) ) {
                    return this.props.history.push( backRoutePayload );
                } else {
                    this.props.history.goBack();
                }

                const queryParams = queryString.parse( this.props.location.search );
                const queryState = get( this.props.location, 'state', {} );

                const checkoutBackRoute = getBackRouteForCheckoutResolver( { queryParams, queryState } );
                this.props.history.push( checkoutBackRoute );
    
                // move to the next step
                // this.props.history.goBack();
    
            } else {
                errorOverlay.open(); // show error
            }
        }
    }

     /**
     * @desc move to previous step.
     */
    async moveToPreviousStep( { url } ) {
        log.debug( 'CheckoutConfirmOrder.moveToPreviousStep()' );

        this.checkoutConfirmOrderService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status } = await this.checkoutConfirmOrderService.moveToPreviousStep( {
            url: url,
            data: {
                ...get( this.state, 'requestPayload' )
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // move to the next step
            this.props.moveBackward();

        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * action executed when user clicks on place order
     */
    async placeOrder( { url, dates, isShipToMD } ) {
        log.debug( 'CheckoutConfirmOrder.placeOrder()' );

        // cancel ongoing request
        this.checkoutConfirmOrderService.cancel();
        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.checkoutConfirmOrderService.placeOrder( {
            url: url,
            data: {
                ...get( this.state, 'requestPayload' ),
                data: {...dates,
                isShipToMD}
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // clear checkout state
        clearRxCheckoutState();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            
            this.setState( {
                isFetching: false,

                // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            }, () => {

                if( ! isEmpty( get( data, 'description' ) ) ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                } else {
                    errorOverlay.open();
                }
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // trigger success view
                MessageBus.trigger( MESSAGE_BUS_EVENTS.SHOW_CHECKOUT_SUCESS_VIEW, data );

            } );
        }
    }

    /**
     * action executed when user clicks on nav action
     */
    handleActionClick( { type, value, promiseDate, needByDate, error, needByAndPromisebyDate, disableDates } ) {
        log.debug( 'CheckoutConfirmOrder.handleActionClick()' );

        if( 'navAction' === type && 'prev' === value.name ) {
            
            // move to previous step
            this.moveToPreviousStep( {
                url: value.url
            } );
        }

        if( 'navAction' === type && 'cancel' === value.name ) {
            
            this.cancelOrder( {
                action: value,
            } );
        }

        if( 'navAction' === type && 'placeOrder' === value.name ) {
            // move to previous step
            if (error) {
                this.setState({ errorMessage: 'Please check the Need by and Promise By Date'})
            } else if (needByAndPromisebyDate && !disableDates) {
                this.placeOrder( {
                    url: value.url,
                    dates: { promiseDate, needByDate },
                    isShipToMD: false
                } );
            } else {
                this.placeOrder( {
                    url: value.url,
                    isShipToMD: true
                } );
            }
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.checkoutConfirmOrderService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CheckoutConfirmOrderView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                errorMessage={ this.state.errorMessage }
                { ...get( this.state, 'componentData', {} ) }
                currentStep={ this.state.currentStep }
                onActionClick={ this.handleActionClick }
                history={ this.props.history }
                memberId={ get( this.state, 'requestPayload.context.memberId', null ) }
                patientId={ get( this.state, 'requestPayload.context.patientId', null ) }
            />
        );
    }
}

// set display name
_CheckoutConfirmOrder.displayName = 'CheckoutConfirmOrder';

// set default props
_CheckoutConfirmOrder.defaultProps = {};

// prop types
_CheckoutConfirmOrder.propTypes = {};

export const CheckoutConfirmOrder = withRouter( _CheckoutConfirmOrder );

